import { useQuery } from "@tanstack/react-query";

import { post } from "./client";
import type { ApiResponse } from "./deals";

/** Current source-record fields rendered beside opaque Planning AI analysis. */
export interface PlanningInsightItemDto {
  planningItemRef: string;
  stableItemId?: string | null;
  planningSnapshotItemId: number;
  sourceActivityId?: string | null;
  title: string;
  lastUpdatedAt?: string | null;
  description?: string | null;
  workstream?: string | null;
  substream?: string | null;
  phase?: string | null;
  owner?: string | null;
  status?: string | null;
  priority?: string | null;
  level?: string | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  isDay1Critical?: boolean | null;
  dependencyText?: string | null;
  riskOfDelay?: string | null;
  impactOfDelay?: string | null;
}

export function getPlanningInsightItems(
  itemIds: readonly string[],
  dealDataVersionId?: number | null
) {
  return post<ApiResponse<PlanningInsightItemDto[]>>("/planning/insight-items/lookup", {
    itemIds,
    dealDataVersionId: dealDataVersionId ?? null,
  });
}

/**
 * Hydrates only the records selected by the insight, keeping live source data
 * separate from the cached AI narrative.
 */
export function useGetPlanningInsightItems(
  itemIds: readonly string[],
  enabled = true,
  dealDataVersionId?: number | null
) {
  const normalizedItemIds = [
    ...new Set(itemIds.map((itemId) => itemId.trim()).filter(Boolean)),
  ].sort();

  return useQuery({
    queryKey: ["api", "planning", "insight-items", dealDataVersionId ?? null, normalizedItemIds] as const,
    enabled:
      enabled && normalizedItemIds.length > 0 && (dealDataVersionId == null || dealDataVersionId > 0),
    queryFn: async () => {
      const response = await getPlanningInsightItems(normalizedItemIds, dealDataVersionId);
      return response.data ?? [];
    },
  });
}
